import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { AppIcon } from '../ui/icon'
import {
    mdiArrowLeft,
    mdiArrowRight,
    mdiChevronDown,
    mdiChevronRight,
    mdiLinkVariant,
    mdiPlus,
    mdiSitemapOutline,
    mdiTrashCanOutline,
} from '@mdi/js'
import { Button } from '../ui/button'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { AddRelationDialog } from './AddRelationDialog'
import { useRelationInvalidation } from './useRelationInvalidation'
import { useLocale } from '../../contexts/LocaleContext'
import { $api } from '../../api/queryClient'
import { useAssetTypes } from '../../api/queries'
import { getLocalizedName } from '../../lib/localization'
import { AssetTypeTile, type AssetTypeLike } from '../assets/AssetTypeTile'
import { apiClient } from '../../api/client'
import {
    filterRelationsBlock,
    groupAssetRelations,
    relationTypeFilter,
    type RelationDirectionGroup,
    type RelationRow,
    type RelationTypeCatalog,
    type RelationTypeGroup,
} from '../../lib/relation-grouping'
import type { components } from '../../api/schema'
import { EmptyState } from '../ui/empty-state'

/**
 * asset_type_code → localized asset type name, so a relation row shows
 * "Radiateur" rather than the raw `hvac_radiator` code. Provided once by the
 * panel (the org asset-type catalogue is already cached app-wide) instead of
 * being drilled through four levels of props.
 */
const AssetTypeLabelContext = createContext<Record<string, string>>({})

/**
 * asset_type_code → the type itself, so a relation row can draw the
 * counterpart's gradient tile (spec 2026-09-17-04). Same one fetch as the
 * labels above; provided beside them rather than drilled through four levels.
 */
const AssetTypeByCodeContext = createContext<Record<string, AssetTypeLike>>({})

/** Localized name of an asset type code, falling back to the code itself when
 *  the catalogue hasn't loaded or doesn't know it. */
function useAssetTypeLabel(code: string | undefined | null): string {
    const labels = useContext(AssetTypeLabelContext)
    if (!code) return ''
    return labels[code] || code
}

/** The asset type behind a code, for the row's tile. */
function useAssetTypeByCode(code: string | undefined | null): AssetTypeLike | undefined {
    const byCode = useContext(AssetTypeByCodeContext)
    if (!code) return undefined
    return byCode[code]
}

type AssetRelationsBlock = components['schemas']['AssetRelationsBlock']
type AssetRelationType = components['schemas']['AssetRelationType']
type PossibleRelationTypeItem = components['schemas']['PossibleRelationTypeItem']

export interface RelationsPanelProps {
    org: string
    assetUid: string
    /** Asset type UID of the focal asset — drives which relation types can be
     *  created. Absent ⇒ read-only panel. */
    assetTypeUid?: string
    /** The relations block embedded on the asset detail response. */
    relations?: AssetRelationsBlock | null
    /** Disambiguates testids when the panel is mounted twice on a page. */
    testIdSuffix?: string
    /** Only these relation types: the listed rows and what « Add » offers
     *  (the Data tab's easements, spec 2026-10-25-24). */
    relationTypeCodes?: string[]
    /** Every relation type but these. */
    excludeRelationTypeCodes?: string[]
    /** Overrides « Linked assets ». */
    title?: string
    /** Overrides « Add a relation » on the button and the dialog title. */
    addLabel?: string
    /** Overrides the empty-state sentence. */
    emptyText?: string
}

/**
 * The focal asset's typed cross-asset links — every edge, both directions,
 * grouped by relation type and direction, plus the "add a relation" action.
 *
 * Everything is catalog-driven (spec 2026-08-16-03 §1/§2): the group order and
 * decoration come from the relation-type catalog's `ordering` / `emoji` /
 * `color`, the direction wording from its `source_role` / `target_role`, the
 * composition-vs-functional split from its `aggregating` flag, and what can be
 * created from `GET /asset-types/{uid}/possible-relation-types`. There is no
 * list of asset-type or relation-type codes anywhere in this component — an
 * org-defined relation type lights up on its own.
 *
 * The structural parent (`parent_asset_uid`) is never shown here — the
 * breadcrumb already owns it.
 */
export function RelationsPanel({
    org,
    assetUid,
    assetTypeUid,
    relations: allRelations,
    testIdSuffix,
    relationTypeCodes,
    excludeRelationTypeCodes,
    title,
    addLabel,
    emptyText,
}: RelationsPanelProps) {
    const { t } = useTranslation('assets')
    const { locale } = useLocale()
    const [addOpen, setAddOpen] = useState(false)

    const includeKey = relationTypeCodes?.join(',') ?? ''
    const excludeKey = excludeRelationTypeCodes?.join(',') ?? ''
    const allowed = useMemo(
        () => relationTypeFilter(includeKey ? includeKey.split(',') : undefined, excludeKey ? excludeKey.split(',') : undefined),
        [includeKey, excludeKey],
    )
    const relations = useMemo(
        () => (includeKey || excludeKey ? filterRelationsBlock(allRelations, allowed) : allRelations),
        [allRelations, allowed, includeKey, excludeKey],
    )

    // Org relation-type catalog: the embedded relation_type_details on each
    // row carries name/emoji/color/roles but NOT `ordering` nor the allowed
    // counterpart asset types, so one cacheable org-level fetch backs both.
    const { data: catalogData } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/asset-relation-types',
        { params: { path: { orgCode: org } } },
    )
    const catalog = useMemo<RelationTypeCatalog>(() => {
        const items = (catalogData as { data?: AssetRelationType[] } | undefined)?.data ?? []
        return Object.fromEntries(items.map((rt) => [rt.code, rt]))
    }, [catalogData])

    // What this asset's type may take part in. Empty / failed ⇒ the panel
    // stays read-only, which is exactly the spec's §1 fallback.
    const { data: possibleData } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/asset-types/{assetTypeUid}/possible-relation-types',
        {
            params: { path: { orgCode: org, assetTypeUid: assetTypeUid as string } },
        },
        { enabled: !!assetTypeUid },
    )
    const possibleTypes = useMemo<PossibleRelationTypeItem[]>(
        () => ((possibleData as { data?: PossibleRelationTypeItem[] } | undefined)?.data ?? []).filter((p) => allowed(p.code)),
        [possibleData, allowed],
    )

    const { composition, functional, total } = useMemo(
        () => groupAssetRelations(relations, locale, catalog),
        [catalog, locale, relations],
    )

    // Asset type codes are machine identifiers; rows must show the localized
    // name from the org catalogue (cached app-wide, shared with every other
    // consumer of useAssetTypes).
    const { data: assetTypes = [] } = useAssetTypes(org)
    const assetTypeLabels = useMemo<Record<string, string>>(() => {
        const out: Record<string, string> = {}
        for (const at of assetTypes as Array<{ code?: string, name?: Record<string, string> }>) {
            if (!at.code) continue
            out[at.code] = getLocalizedName(at.name, locale) || at.code
        }
        return out
    }, [assetTypes, locale])

    const assetTypesByCode = useMemo<Record<string, AssetTypeLike>>(() => {
        const out: Record<string, AssetTypeLike> = {}
        for (const at of assetTypes as AssetTypeLike[]) {
            if (at.code) out[at.code] = at
        }
        return out
    }, [assetTypes])

    const suffix = testIdSuffix ? `-${testIdSuffix}` : ''
    const addText = addLabel ?? t('relations.add_title', { defaultValue: 'Add a relation' })
    const canAdd = possibleTypes.length > 0
    // A filtered panel (easements…) the asset type cannot take part in, with
    // nothing to show, is noise: render nothing.
    if ((includeKey || excludeKey) && total === 0 && !canAdd) return null

    return (
        <AssetTypeLabelContext.Provider value={assetTypeLabels}>
        <AssetTypeByCodeContext.Provider value={assetTypesByCode}>
        <section
            className="rounded-lg border border-border bg-card p-4 space-y-5"
            data-testid={`asset-relations-panel${suffix}`}
        >
            <header className="flex items-center gap-2">
                <AppIcon tone="muted" path={mdiLinkVariant} size="md" />
                <h2 className="text-base font-medium text-foreground">
                    {title ?? t('relations.section_title', { defaultValue: 'Linked assets' })}
                </h2>
                {total > 0 && (
                    <span className="text-xs text-muted-foreground">({total})</span>
                )}
                <div className="ml-auto">
                    {canAdd && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setAddOpen(true)}
                            aria-label={addText}
                            title={addText}
                            data-testid="relations-add-btn"
                        >
                            <AppIcon path={mdiPlus} size="sm" className="md:mr-1" />
                            <span className="hidden md:inline">{addText}</span>
                        </Button>
                    )}
                </div>
            </header>

            {total === 0 && (
                <EmptyState
                    variant="inline"
                    icon={mdiLinkVariant}
                    title={
                        emptyText
                            ? emptyText
                            : canAdd
                            ? t('relations.empty_with_add', {
                                defaultValue: 'No link yet. Use “Add a relation” to link this asset to another one.',
                            })
                            : t('relations.empty_readonly', {
                                defaultValue: 'No link, and this asset type takes part in no relation type.',
                            })
                    }
                    data-testid="relations-empty"
                />
            )}

            {composition.length > 0 && (
                <RelationSection
                    testId="relations-section-composition"
                    title={t('relations.composition_title', { defaultValue: 'Composition' })}
                    groups={composition}
                    org={org}
                    focalAssetUid={assetUid}
                />
            )}

            {functional.length > 0 && (
                <RelationSection
                    testId="relations-section-functional"
                    title={t('relations.functional_title', { defaultValue: 'Functional links' })}
                    groups={functional}
                    org={org}
                    focalAssetUid={assetUid}
                />
            )}

            {canAdd && (
                <AddRelationDialog
                    org={org}
                    assetUid={assetUid}
                    open={addOpen}
                    onOpenChange={setAddOpen}
                    possibleTypes={possibleTypes}
                    catalog={catalog}
                    title={addLabel}
                />
            )}
        </section>
        </AssetTypeByCodeContext.Provider>
        </AssetTypeLabelContext.Provider>
    )
}

interface RelationSectionProps {
    testId: string
    title: string
    groups: RelationTypeGroup[]
    org: string
    focalAssetUid: string
}

function RelationSection({ testId, title, groups, org, focalAssetUid }: RelationSectionProps) {
    return (
        <div className="space-y-3" data-testid={testId}>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                {title}
            </h3>
            {groups.map((group) => (
                <RelationTypeSubsection
                    key={group.code}
                    group={group}
                    org={org}
                    focalAssetUid={focalAssetUid}
                />
            ))}
        </div>
    )
}

interface RelationTypeSubsectionProps {
    group: RelationTypeGroup
    org: string
    focalAssetUid: string
}

function RelationTypeSubsection({ group, org, focalAssetUid }: RelationTypeSubsectionProps) {
    const { t } = useTranslation('assets')
    const [open, setOpen] = useState(true)

    const aggregatesLabel = t('relations.aggregates', { defaultValue: 'rolls up' })
    const aggregatesHint = t('relations.aggregates_hint', {
        defaultValue:
            'Aggregating link: the component also counts as part of the assembly in ancestor-scoped listings.',
    })

    return (
        <div
            className="space-y-2 rounded-md border border-border/60 p-3"
            data-testid={`relations-group-${group.code}`}
        >
            <button
                type="button"
                className="flex w-full items-center gap-1.5 text-left text-sm font-medium text-foreground"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                data-testid={`relations-group-${group.code}-toggle`}
            >
                <AppIcon tone="muted" path={open ? mdiChevronDown : mdiChevronRight} size="sm" className="shrink-0" aria-hidden />
                {group.color && (
                    <span
                        className="h-3 w-3 rounded-full shrink-0"
                        style={{ backgroundColor: group.color }}
                        aria-hidden
                    />
                )}
                {group.emoji && <span aria-hidden>{group.emoji}</span>}
                <span>{group.name}</span>
                <span className="text-xs text-muted-foreground">({group.rows.length})</span>
                {group.aggregating && (
                    <span
                        className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-normal text-muted-foreground"
                        title={aggregatesHint}
                        data-testid={`relations-group-${group.code}-aggregating`}
                    >
                        {aggregatesLabel}
                    </span>
                )}
            </button>

            {open && group.directions.map((dir) => (
                <RelationDirectionList
                    key={dir.direction}
                    group={group}
                    dir={dir}
                    org={org}
                    focalAssetUid={focalAssetUid}
                />
            ))}
        </div>
    )
}

interface RelationDirectionListProps {
    group: RelationTypeGroup
    dir: RelationDirectionGroup
    org: string
    focalAssetUid: string
}

function RelationDirectionList({ group, dir, org, focalAssetUid }: RelationDirectionListProps) {
    const { t } = useTranslation('assets')

    // Incoming edges of an aggregating type are the assembly's members — the
    // "Composed of" view. The rollup writes `parent_asset_ids`, so the
    // ancestor-scoped asset list is the way to see the whole network content
    // without duplicating members in the tree (§5).
    const showMembersLink = group.aggregating && dir.direction === 'incoming'

    return (
        <div
            className="space-y-1 pl-5"
            data-testid={`relations-group-${group.code}-${dir.direction}`}
        >
            <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                <AppIcon
                    path={dir.direction === 'outgoing' ? mdiArrowRight : mdiArrowLeft}
                    size="xs"
                    className="shrink-0"
                    aria-hidden
                />
                {/* Article-free on purpose: the role comes from the catalog
                    as a free-form localized noun, and no frontend can know its
                    gender or initial phoneme, so it is never preceded by an
                    article. The label names the COUNTERPART's role — what the
                    assets listed below actually are ("Dessert · desservi") —
                    and the arrow above carries the direction. */}
                <span>
                    {t('relations.heading', {
                        name: group.name,
                        role: dir.counterpartRole,
                        defaultValue: '{{name}} · {{role}}',
                    })}
                </span>
                <span>({dir.rows.length})</span>
                {showMembersLink && (
                    <Link
                        to="/$org/assets"
                        params={{ org }}
                        search={{ ancestor_uid: focalAssetUid } as never}
                        className="text-primary hover:underline inline-flex items-center gap-1"
                        data-testid={`relations-group-${group.code}-members-link`}
                    >
                        <AppIcon path={mdiSitemapOutline} size="xs" aria-hidden />
                        <span>
                            {t('relations.see_all_members', {
                                defaultValue: 'see all equipment',
                            })}
                        </span>
                    </Link>
                )}
            </div>
            <ul className="space-y-1">
                {dir.rows.map((row) => (
                    <RelationRowItem
                        key={row.uid}
                        row={row}
                        group={group}
                        org={org}
                        focalAssetUid={focalAssetUid}
                    />
                ))}
            </ul>
        </div>
    )
}

interface RelationRowItemProps {
    row: RelationRow
    group: RelationTypeGroup
    org: string
    focalAssetUid: string
}

function RelationRowItem({ row, group, org, focalAssetUid }: RelationRowItemProps) {
    const { t } = useTranslation('assets')
    const invalidate = useRelationInvalidation(org)
    const assetTypeLabel = useAssetTypeLabel(row.counterpart.asset_type_code)
    const counterpartType = useAssetTypeByCode(row.counterpart.asset_type_code)
    const [confirming, setConfirming] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [errorMsg, setErrorMsg] = useState<string | null>(null)

    const confirmDelete = useCallback(async () => {
        setDeleting(true)
        try {
            const { error } = await apiClient.DELETE(
                '/api/v1/organizations/{orgCode}/asset-relations/{relationUid}',
                { params: { path: { orgCode: org, relationUid: row.uid } } } as never,
            )
            if (error) throw error
            invalidate([focalAssetUid, row.counterpart.uid])
            setConfirming(false)
            setErrorMsg(null)
        } catch (err) {
            setErrorMsg(err instanceof Error ? err.message : String(err))
        } finally {
            setDeleting(false)
        }
    }, [focalAssetUid, invalidate, org, row.counterpart.uid, row.uid])

    const removeLabel = t('common:actions.remove', { defaultValue: 'Remove' })

    return (
        <li
            className="flex flex-wrap items-center gap-1.5 text-sm"
            data-testid={`relation-row-${group.code}-${row.uid}`}
            data-relation-uid={row.uid}
        >
            {row.counterpart.uid ? (
                <Link
                    to="/$org/assets/$assetUid"
                    params={{ org, assetUid: row.counterpart.uid }}
                    className="text-primary hover:underline inline-flex items-center gap-1.5"
                >
                    <AssetTypeTile type={counterpartType} size="sm" />
                    <span>{row.counterpart.name || row.counterpart.uid}</span>
                </Link>
            ) : (
                <span className="italic text-muted-foreground">
                    {t('relations.unknown_target', { defaultValue: '(unknown)' })}
                </span>
            )}
            {row.counterpart.asset_type_code && (
                <span
                    className="rounded-full border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground"
                    title={row.counterpart.asset_type_code}
                    data-asset-type-code={row.counterpart.asset_type_code}
                >
                    {assetTypeLabel}
                </span>
            )}
            <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive"
                aria-label={removeLabel}
                title={removeLabel}
                onClick={() => setConfirming(true)}
                data-testid="relation-row-delete"
            >
                <AppIcon path={mdiTrashCanOutline} size="sm" />
            </Button>
            {errorMsg && (
                <span className="text-xs text-destructive" data-testid="relation-row-delete-error">
                    {errorMsg}
                </span>
            )}
            <AlertDialog open={confirming} onOpenChange={setConfirming}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {t('relations.delete_title', { defaultValue: 'Remove link?' })}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('relations.delete_description', {
                                defaultValue:
                                    'This removes the link between the two assets. The assets themselves are not affected.',
                            })}
                            {group.aggregating
                                ? ` ${t('relations.delete_aggregating_note', {
                                    defaultValue:
                                        'This is an aggregating link: the component will leave the assembly’s rollup and stop appearing in its ancestor-scoped listings.',
                                })}`
                                : ''}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="relation-row-delete-cancel">
                            {t('common:actions.cancel', { defaultValue: 'Cancel' })}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDelete}
                            disabled={deleting}
                            data-testid="relation-row-delete-confirm"
                        >
                            {removeLabel}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </li>
    )
}
